import React from "react";
import { formatNumber } from "../../utils/functions/formatNumber";
import { getMonthText } from "../functions/getMonthText";
import { equalsZero } from "../functions/equalsZero";

/**
 * pais: current pais
 * status: incremento/ disminucion de la conversacion
 * increment: %total de conversaciones
 * statusComparativo: debajo/arriba del nivel registrado
 * cifra: % migracion
 */
export const migTweets = {
  boxTitle: "Conversation About Migration on Twitter",
  dataSubtitle: (isMostRecentYear) => {
    return isMostRecentYear
      ? "Number of tweets about migration"
      : "Monthly average number of tweets about migration";
  },
  description: (isMostRecentYear, value, time, change) => {
    let comparisonSentence;
    comparisonSentence = equalsZero(change)
      ? "This represented no change compared to the previous " +
        (isMostRecentYear ? "month." : "year.")
      : "This represented " +
        (change < 0 ? "a decrease" : "an increment") +
        " compared to the previous " +
        (isMostRecentYear ? "month" : "year") +
        " of " +
        (change ?? "--") +
        "%.";
    return (
      <span>
        By analyzing the volume of tweets we can get an idea of ​​how prevalent
        the topic is in social media over time. Tweets related to migration
        accounted for {value ? formatNumber(value, "en") : "--"} in{" "}
        {isMostRecentYear ? getMonthText(time, "en") : time}.{" "}
        {comparisonSentence}
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        To collect tweets related to the migratory issue, a series of queries
        that capture migratory content was defined. The set of tweets from these
        queries was filtered to exclude content related to the topic but not
        belonging to the region, as well as false positives that are not related
        to the topic. To assign a geographical location to each tweet (country
        or city), the location provided by Twitter users was used, either as a
        geographical point (latitude and longitude) or as the name of the city
        in which the account is registered. Since not all tweets can be
        geo-located, the total number of tweets assigned to each country or city
        is therefore less than the actual volume emitted in each geography. This
        standardized methodology for all the geographies analyzed allows,
        however, to make a comparison between countries. The geo-location
        process was standardized using the{" "}
        <a
          href="https://www.here.com/?cid=Brand-PureBrand-Google-YT-0-Here-AMER-T1&utm_source=Google&utm_medium=ppc&utm_campaign=IOT_Paidsearch_GenericProduct_AlwaysOn&utm_term=here&gclid=CjwKCAjwgr6TBhAGEiwA3aVuIY1CRORTgk0unKxtKKkhrgw_RKmlufIqzw2Pu_oz-oBbnB8SDKgg6hoCUzkQAvD_BwE&gclsrc=aw.ds"
          target="_blank"
          rel="noreferrer"
        >
          Here
        </a>{" "}
        API. The methodology to carry out this mining can be consulted
        <a
          href="http://www.metodologiaminado.com/"
          target="_blank"
          rel="noreferrer"
        >
          {" "}
          here
        </a>
        .
      </span>
    );
  },
};

export const migNews = {
  boxTitle: "Percentage of News on Migration",
  dataSubtitle: "Percentage of news articles about migration",
  description: (isMostRecentYear, time, value, change) => {
    let comparisonSentence;
    comparisonSentence = equalsZero(change)
      ? "This represented no change compared to the previous " +
        (isMostRecentYear ? "month." : "year.")
      : "This represented a change of " +
        (change ?? "--") +
        "pp compared to the previous " +
        (isMostRecentYear ? "month." : "year.");

    return (
      <span>
        In order to have a measure of the news importance of the migratory
        topic, the percentage of news about migration is presented for the
        region. In {isMostRecentYear ? getMonthText(time, "en") : time}, the
        news related to migration accounted for {value ?? "--"}%.{" "}
        {comparisonSentence}
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        The source of information for news related to migration is
       {" "}
        <a
          href="https://newsapi.org/"
          target="_blank"
          rel="noreferrer"
        >
          NewsAPI
        </a>
        {" "}from EventRegistry. This resource tracks and captures the most relevant information sources from each country at all times. In the process of detecting events, NewsAPI mines, translates, processes, classifies articles into categories, and extracts concepts, which could be people, organizations, and non-entities. With
        {" "}
        <a
          href="https://newsapi.org/docs"
          target="_blank"
          rel="noreferrer"
        >
          NewsAPI
        </a>
        , it is possible to filter articles containing concepts related to human migration, resulting in a monthly aggregate.
      </span>
    );
  },
};

export const principalTopics = {
  boxTitle: (isXeno) => {
    return isXeno
      ? "Principal Topics in Xenophobic Conversation on Twitter"
      : "Principal Topics on Twitter About Migration";
  },

  dataSubtitle: "Most frequent topic",
  description: (isXeno, year, month, topic, percentage) => {
    let timePeriodText = "";

    if (month !== "ALL") {
      timePeriodText += "during the " + getMonthText(month, "en") + " of ";
    } else {
      timePeriodText += "in ";
    }

    timePeriodText += year;

    return isXeno ? (
      <span>
        In the same way that the analysis was done for all migratory tweets, the
        thematic structure of the tweets classified as xenophobic was analyzed,
        using the same categories mentioned above.
      </span>
    ) : (
      <span>
        Using a model that identifies thematic content in the conversation,
        tweets were classified according to different categories. The main topic
        of conversation {timePeriodText} was {topic ? topic : "--"}, which
        represented {percentage ? percentage : "--"}% of the total conversation.
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        To thematically classify tweets, a clustering model known as{" "}
        <a
          href="https://maartengr.github.io/BERTopic/index.html"
          target="_blank"
          rel="noreferrer"
        >
          BERTopic
        </a>{" "}
        was trained in a semi-supervised manner. The training set consisted of
        292,000 randomly selected texts within the time period from 2017 to
        2022. To avoid a thematic bias, the random sample consisted of
        approximately 13,695 texts for the 18 countries with the highest number
        of tweets, while for the eight countries with the lowest presence, the
        number of available tweets was taken. A total of 24,000 tagged texts
        with the topics COVID-19, Employment, Gender, Security and Public
        Services were added to the random sample. Since BERTopic produces
        semantically similar groups of texts, it is able to find topics
        different from those defined by the labeled data. The resulting model
        detected a total of 271 clusters, which were manually analyzed in order
        to obtain a relationship between sets of clusters and the topics of
        interest. The percentages shown were calculated from the number of texts
        belonging to each topic divided by the total number of tweets belonging
        to one of the 271 groups.
      </span>
    );
  },
};

export const convTransformation = {
  boxTitle:
    "Evolution of the Conversation about Migration on Twitter over Time",
  dataSubtitle: (currentCountry, selectedTopic, topicOptions) => {
    return currentCountry === "LAC"
      ? "Percentage of conversation on Twitter that talks about " +
          (topicOptions && topicOptions.find((t) => t.code === selectedTopic)
            ? topicOptions.find((t) => t.code === selectedTopic).label
            : "--") +
          " in Latin America and the Caribbean"
      : "Percentage of conversation on Twitter about most frequent topic";
  },
  description: () => {
    return (
      <span>
        Using the same model of topic classification, this chart analyzes the
        evolution over time of topics.
      </span>
    );
  },
  methodology:
    "Using the same classification model described before, the themes observed at each time point were assigned, and with this information the thematic percentage structure was plotted over time. For the historical series, a quarterly grouping is presented. Otherwise, the grouping is monthly.",
};

export const cityActivity = {
  boxTitle: "Tweet Volume Related to Migration by City",

  dataSubtitle: (dataType) => {
    let text;

    switch (dataType) {
      case "all":
        text =
          "City with the most active Twitter users as a rate per 10,000 inhabitants";
        break;
      case "xeno":
        text =
          "City with the most xenophobic tweet activity as a rate pero 10,000 habitants";
        break;
      case "all_volume":
        text = "City with the most activity on Twitter";
        break;
      case "xeno_volume":
        text = "City with the most xenophobic activity on twitter";
        break;
      default:
        text = "";
    }

    return text;
  },
  description: `Activity in the migration conversation is concentrated in major cities but varies from city to city. This map shows the total activity in each city (as the total count of tweets), disaggregated by its xenophobic conent. To compare cities regardless of its population size, conversation activity is also presented as Tweets per 10,000 inhabitants.`,
  methodology: () => {
    return (
      <span>
        Once a geographical location (city and country) has been assigned as
        described in the “conversation volume” section, tweets were counted both
        by country and by city. In the case of cities, we counted the tweets
        within the polygons of urban areas defined by{" "}
        <a
          href="https://earthworks.stanford.edu/catalog/stanford-yk247bg4748"
          target="_blank"
          rel="noreferrer"
        >
          Kelso, N.V. and Patterson, T. (2012). World Urban Areas, LandScan,
          1:10 million (2012)
        </a>
        . These urban areas privilege their regional and population importance
        on administrative boundaries. To get the rates of tweets per every
        10,000 inhabitants, the population estimate of this source of
        information was used, which estimates the population of urban areas
        using a population raster from Oak Ridge National Laboratory's LandScan.
        The data that shows the rate of xenophobic tweets only considers cities
        with more than 100,000 inhabitants where there were more than 10 tweets
        in that quarter.
      </span>
    );
  },
  resources: {
    1: "",
  },
};

export const migratorySentiment = {
  boxTitle: "Sentiment Towards Migration Over Time",
  dataSubtitle: "Percentage of positive conversation",
  description: (
    isMostRecentYear,
    time,
    value,
    negValue,
    posChange,
    negChange
  ) => {
    let comparisonText =
      "Compared to the previous " + (isMostRecentYear ? "month" : "year");

    if (equalsZero(posChange) && equalsZero(negChange)) {
      comparisonText +=
        ", neither the positive nor the xenophobic conversation changed.";
    } else {
      comparisonText += ", the positive conversation ";

      if (equalsZero(posChange)) {
        comparisonText += "did not change";
      } else {
        comparisonText += posChange > 0 ? "increased " : "decreased ";
        comparisonText += posChange + "pp";
      }

      comparisonText += ", while the xenophobic conversation ";

      if (equalsZero(negChange)) {
        comparisonText += "did not change.";
      } else {
        comparisonText += negChange > 0 ? "increased " : "decreased ";
        comparisonText += negChange + "pp.";
      }
    }

    return isMostRecentYear ? (
      <span>
        When considering all tweets, positive conversation represented{" "}
        {value ?? "--"}% in {time ? getMonthText(time, "en") : "--"}, while the
        xenophobic conversation represented {negValue ?? "--"}%.{" "}
        {comparisonText}
      </span>
    ) : (
      <span>
        When considering all tweets, positive conversation represented{" "}
        {value ?? "--"}%, while xenophobic conversation represented{" "}
        {negValue ?? "--"}%.
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        <p>
          A sentiment analysis classifies tweets as 'positive' or 'neutral',
          depending on their polarity or emotional tone, and gives us an idea of
          ​​the way in which citizens express opinions about migration over
          time. Regularly, by tone or use of words with which it is expressed, a
          tweet with positive feeling or polarity will tend to have contents
          that are in favor of migrants, and the opposite occurs with tweets
          classified as xenophobic. Neutral tweets do not express any tone in
          their polarity, as is the case of many news with comments.
        </p>
        <p>
          To classify the sentiment of migratory tweets, we used the RoBERTuito
          model, a pre-trained BERT model from the repository of Hugging Face
          models, trained to perform a feeling classification. The choice of
          this model is due to the similarity of the problem to model. With a
          tagged database of the IDB, a fine-tuning of the model was carried
          out. The full description of the methodology and evaluation can be
          consulted{" "}
          {/* <a
            href="https://es.overleaf.com/project/621669b06ad8e74c5b4366e1"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a> */}
          .
        </p>
      </span>
    );
  },
};

export const migratorySentimentbyTopic = {
  boxTitle: "Migratory Sentiment on Twitter by Topic",
  dataSubtitle: "Percentage of positive conversation",
  description: (
    selectedTopic,
    isMostRecentYear,
    time,
    value,
    negValue,
    posChange,
    negChange
  ) => {
    let comparisonText =
      "Compared to the previous " + (isMostRecentYear ? "month" : "year");

    if (equalsZero(posChange) && equalsZero(negChange)) {
      comparisonText +=
        ", neither the positive nor the xenophobic conversation changed.";
    } else {
      comparisonText += ", the positive conversation ";

      if (equalsZero(posChange)) {
        comparisonText += "did not change";
      } else {
        comparisonText += posChange > 0 ? "increased " : "decreased ";
        comparisonText += posChange + "pp";
      }

      comparisonText += ", while the xenophobic conversation ";

      if (equalsZero(negChange)) {
        comparisonText += "did not change.";
      } else {
        comparisonText += negChange > 0 ? "increased " : "decreased ";
        comparisonText += negChange + "pp.";
      }
    }

    return isMostRecentYear ? (
      <span>
        When considering all tweets related to {selectedTopic}, positive
        conversation represented {value ?? "--"}% in{" "}
        {time ? getMonthText(time, "en") : "--"}, while the xenophobic
        conversation represented {negValue ?? "--"}%. {comparisonText}
      </span>
    ) : (
      <span>
        When considering all tweets related to {selectedTopic}, positive
        conversation represented {value ? value : "--"}%, while xenophobic
        conversation represented {negValue ? negValue : "--"}%.
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        <p>
          A sentiment analysis classifies tweets as 'positive' or 'neutral',
          depending on their polarity or emotional tone, and gives us an idea of
          ​​the way in which citizens express opinions about migration over
          time. Regularly, by tone or use of words with which it is expressed, a
          tweet with positive feeling or polarity will tend to have contents
          that are in favor of migrants, and the opposite occurs with tweets
          classified as xenophobic. Neutral tweets do not express any tone in
          their polarity, as is the case of many news with comments.
        </p>
        <p>
          To classify the sentiment of migratory tweets, we used the RoBERTuito
          model, a pre-trained BERT model from the repository of Hugging Face
          models, trained to perform a feeling classification. The choice of
          this model is due to the similarity of the problem to model. With a
          tagged database of the IDB, a fine-tuning of the model was carried
          out. The full description of the methodology and evaluation can be
          consulted{" "}
          {/* <a
            href="https://es.overleaf.com/project/621669b06ad8e74c5b4366e1"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a> */}
          .
        </p>
      </span>
    );
  },
};

export const comparativeMapSent = {
  boxTitle: (selectedSentiment) =>
    "Comparative Map of " +
    (selectedSentiment === "ositive" ? "Positive" : "Neutral") +
    " Migratory Sentiment on Twitter ",
  dataSubtitle: (country, selectedSentiment) =>
    `Registered the largest percentage of ` +
    (selectedSentiment === "positive" ? "positive" : "neutral") +
    " tweets",
  description:
    "This map presents a sentiment comparison in different time cuts for all the countries analyzed.",
  methodology: () => {
    return (
      <span>
        A sentiment analysis classifies tweets as 'positive' or 'neutral',
        depending on their polarity or emotional tone, and gives us a idea of
        ​​how citizens express opinions on the migration topic over time.
        Regularly, by the tone or use of words with which it is expressed, a
        tweet with positive feeling or polarity will tend to have content that
        is in favor of migrants; the opposite occurs with tweets classified as
        xenophobic. The neutral tweets do not express any tone in their
        polarity, as is the case with lots of news with comments. To classify
        the feeling of the migratory tweets, the{" "}
        <a
          href="https://huggingface.co/pysentimiento/robertuito-sentiment-analysis"
          target="_blank"
          rel="noreferrer"
        >
          RoBERTuito model
        </a>
        was used, a pre-trained BERT model from the Hugging Face model
        repository, trained to perform sentiment classification. This model's
        choice obeys the similarity of the problem to be modelled. With a tagged
        database of the IDB, a fine-tuning was made of the model. The complete
        description of the methodology and evaluation can be consult{" "}
        {/* <a
          href="https://es.overleaf.com/project/621669b06ad8e74c5b4366e1"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a> */}
        .
      </span>
    );
  },
};

export const xenoPrevalence = {
  boxTitle: "Xenophobic Conversation on Twitter",
  dataSubtitle: "Xenophobic prevalence",
  description: (isMostRecentYear, time, data, lacData, isLAC) => {
    let comparison;
    if (parseInt(data) < parseInt(lacData)) comparison = "smaller";
    else comparison = "larger";

    return isLAC ? (
      <span>
        Xenophobic prevalence is obtained by dividing the number of tweets with
        xenophobic content by the number of migratory tweets in a period. In{" "}
        {isMostRecentYear ? getMonthText(time, "en") : time}, xenophobic
        expressions in the conversation about migration represented{" "}
        {data ? data : "--"}%.
      </span>
    ) : (
      <span>
        Xenophobic prevalence is obtained by dividing the number of tweets with
        xenophobic content by the number of migratory tweets in a period. In{" "}
        {isMostRecentYear ? getMonthText(time, "en") : time}, xenophobic
        expressions in the conversation about migration represented{" "}
        {data ? data : "--"}%, which is {comparison} as compared to the region
        as a whole ({lacData ? lacData : "--"}%).
      </span>
    );
  },
  methodology: () => {
    return (
      <span>
        To classify a tweet as xenophobic, the{" "}
        <a
          href="https://huggingface.co/pysentimiento/robertuito-sentiment-analysis"
          target="_blank"
          rel="noreferrer"
        >
          RoBERTuito model
        </a>
        which is a pre-trained BERT model from the Hugging Face model
        repository, trained to perform hate speech classification. The choice of
        this model is due to the similarity of the problem that's to be modeled.
        The fine-tuning of the model was performed with a set of tweets labeled
        as xenophobic. For more details on this model and its evaluation
        metrics, please consult{" "}
        <a
          href="https://drive.google.com/file/d/1E-2Geoa_Ll-7PdkgkVJWOZCuU54TPqAK/view"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>
        .
      </span>
    );
  },
};

export const reach = {
  boxTitle: "Potential Reach of Migratory Tweets",
  dataSubtitle: "Number of accounts reached by positive tweets",
  description: (isMostRecentYear, time, data, negData) => {
    return (
      <span>
        Average reach per tweet was estimated by dividing the number of
        followers whose accounts posted positive or xenophobic tweets by the
        number of positive (or xenophobic) tweets in a period. In{" "}
        {isMostRecentYear ? getMonthText(time, "en") : time}, a positive tweet
        had a potential reach of {data ? formatNumber(data, "en") : "--"}{" "}
        accounts, while a xenophobic tweet has a potential reach of{" "}
        {negData ? formatNumber(negData, "en") : "--"} accounts.
      </span>
    );
  },
  methodology:
    "Average tweet reach is estimated as the sum of the followers of all accounts that tweeted with positive or xenophobic content, divided by the number of tweets posted by those accounts. The high reach of positive tweets is explained by the publication of a greater number of positive tweets, as well as the fact that there are many accounts with a very large number of followers that publish positive content.",
};

//2.3 Reacción tweets de cuentas migratorias institucionales
export const reactionMigAccounts = {
  boxTitle: "Reaction to Tweets from Institutional Migration Accounts",
  dataSubtitle: (isMostRecentYear) => {
    return isMostRecentYear
      ? "Percentage of positive reactions"
      : "Monthly average of positive reactions percentage";
  },
  description:
    "For each country, a set of institutional accounts (immigration institutes, consulates, research centers) that produce content on the subject was mined. The reactions they generated (i.e. responses) were classified according to their sentiment.",
  methodology:
    "Each response to a migratory tweet was analyzed with the sentiment classifier described above. Once classified, a grouping was made by their sentiment. The sentiment classification model is the same as described above.",
};

export const comparativeMapXeno = {
  boxTitle: "Comparative Map of Xenophobia Prevalence on Twitter",
  dataSubtitle: "Country with the least xenophobic prevalence",
  description:
    "The map presents a comparison of the prevalence of xenophobia in a cut of time for all the countries analyzed.",
  methodology:
    "The classification of xenophobic tweets was carried out using a model like the one described above. For further details please consult XXX.",
};

export const xenoAccounts = {
  boxTitle: "Xenophobic Accounts on Twitter",
  dataSubtitle: "Percentage of xenophobic accounts",
  description: (isMostRecentYear, time, change) => {
    let comparisonText =
      "In " + (isMostRecentYear ? getMonthText(time, "en") : time) + ", ";

    if (equalsZero(change)) {
      comparisonText += "there was no change ";
    } else {
      let changeText;
      if (change) {
        changeText = change > 0 ? "an increase" : "a decrease";
      } else {
        changeText = "--";
      }

      comparisonText += "there was ";
      comparisonText += changeText + " of " + (change ?? "--") + "pp";
    }

    comparisonText +=
      " in xenophobic accounts compared to the previous " +
      (isMostRecentYear ? "month." : "year.");

    return (
      <span>
        The chart presents the percentage of accounts that published xenophobic
        content in relation to the accounts that tweeted about migration.{" "}
        {comparisonText}
      </span>
    );
  },
  methodology:
    "An account is classified as xenophobic when it publishes a number of xenophobic tweets greater than the average number of xenophobic tweets per user in that month, in that geography. Once these accounts have been classified, their number is divided by the total number of accounts that published migratory content.",
};

export const xenoReach = {
  boxTitle: "Reach of Xenophobic Tweets",
  dataSubtitle: "Número de cuentas alcanzadas con tweets xenófobos",
  description: (month, reach, change) => {
    return (
      <span>
        En {month || "enero"}, los contenidos xenófobos tuvieron un alcance
        potencial de {formatNumber(reach, "en") || "15,033"} cuentas, lo que
        representó {change < 0 ? "una disminución" : " un incremento"} de{" "}
        {change || 20}%
      </span>
    );
  },
  methodology:
    "El alcance de tweets xenófobos se estimó como  la suma de los seguidores de todas las cuentas únicas que realizaron un tweet con contenido migratorio. Esta estimación se realizó para tweets clasificados como positivos o xenófobos.",
};

export const perceptionsEMV = {
  boxTitle: "World Values Survey",
  dataSubtitle: "",
  description:
    "The World Values ​​Survey (WSS) is a global research project on the public opinion of the majority of the world's population. In this section you can consult a battery of questions regarding attitudes towards migrants in Latin American countries, and analyze the answers by making sociodemographic cuts.",
  methodology:
    "Three available waves or cuts were analyzed. Not all countries appear in all three waves. Estimates were made using the expansion factor provided in the survey.",
};

export const perceptionsLatino = {
  boxTitle: "Latinobarómetro",
  dataSubtitle: "",
  description:
    "Latinobarómetro is a public opinion survey carried out each year in 18 Latin American countries. Similar to the EMV, questions related to the attitude of Latin Americans towards migrants were selected, and cuts can be made by age, gender, occupation and others. The years covered by the analysis are 2015, 2018, 2020 and 2023.",
  methodology:
    "The three most recent years were analyzed. Estimates were made using the expansion factor provided in the survey.",
};

export const perceptionsLAPOP = {
  boxTitle: "Americas Barometer (LAPOP)",
  dataSubtitle: "",
  description:
    'LAPOP is the premier academic institution carrying out surveys of public opinion in the Americas, with over thirty years of experience. As a center for excellence in survey research, LAPOP uses "gold standard" approaches and innovative methods to carry out targeted national surveys; conduct impact evaluation studies; and produce reports on individual attitudes, evaluations, and experiences. The AmericasBarometer survey is the only scientifically rigorous comparative survey that covers 34 nations including all of North, Central, and South America, as well as a significant number of countries in the Caribbean. Each year it publishes dozens of high quality academic studies and policy-relevant papers.',
  methodology: "",
};

export const comunidades = {
  description:
    "Para comprender las conversaciones al interior de las comunidades en Twitter y Facebook, se hicieron dos análisis de comunidades. Por un lado, se analizaron las conversaciones de grupos públicos de Facebook. En segundo lugar, se identificaron las cuentas de Twitter que conversan entre ellas y forman comunidades orgánicas.",
};

export const facebookMembers = {
  boxTitle: "Facebook Migrant Groups",
  dataSubtitle: "Group with the most members",
  description:
    "Facebook groups for migrants in different countries were analyzed and ordered by number of members.",
  methodology: "",
};

export const facebookWordcloud = {
  boxTitle: "Key Words Used in Facebook Groups",
  dataSubtitle: "Most used word",
  description:
    "Analyzing posts and their responses on the group pages helps us understand if these platforms are used to generate coordination or help mechanisms within said groups. Here is a wordcloud with the most used words in conversations.",
  methodology: "",
};

export const twitterWordcloud = {
  boxTitle: "Key Hashtags Used in Twitter Communities",
  dataSubtitle: "Most used hashtag",
  description:
    "12 major communities were identified using a community detection algorithm. For each community, the most used hashtags were analyzed.",
  methodology:
    "Using the community detection algorithm of Blondel et al. (2008), a graph was created and the most important communities of conversations about migration were identified. For more information, consult (REPORT).",
};
